/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { AdvisoryStats } from '../home/AdvisoryStats';
import { AlertsList } from '../Ipaws/IPAWSAlertList';
import { FlyToInterpolator } from '@deck.gl/core';
import { initialViewState, viewStateAtom, viewingHistoricalAtom } from '../map/atoms';
import { Avatar, Paper, Tooltip, Typography } from '@mui/material';
import { useMatch, useSearchParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import Box from '@mui/material/Box';
import React from 'react';
import { useNifcLayer } from '../NIFC/NifcLayer';
import { PrimaryLayer } from './MapPage';
import { ReactComponent as NifcLogo } from '../NIFC/nifc-logo.svg';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { enableCanopy } from '../constants';
import { IncidentLists } from '../home/IncidentLists';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import { useWowLayer } from '../wow/WowLayer';
import { visualModeAtom } from '../map/atoms';
import { useCanopyLayer } from '../canopy/CanopyLayer';
import canopyLogo from '../images/canopy-logo.png';
import { useCanopySwathLayer } from '../canopy/CanopySwathLayer';
import { useTingOutagesLayer } from '../ting/TingOutagesLayer';
import { TingOutageList } from '../ting/TingOutageList';
import { ReactComponent as TingLogo } from '../ting/ting-logo.svg';
import { ErrorBoundary } from '../components/ErrorBoundary';

export const useHomeLayers = (props: { selectedMap: PrimaryLayer }) => {
  const visualMode = useAtomValue(visualModeAtom);
  const setViewState = useSetAtom(viewStateAtom);
  const match = useMatch({ path: '/' });
  const [searchParams] = useSearchParams();
  // A historical view (time slider / day summary) renders the _history tables
  // via useWeatherEventsLayer; the live current-table layers hide so the two
  // never draw on top of each other.
  const viewingHistorical = useAtomValue(viewingHistoricalAtom);
  const timeseriesEnabled = searchParams.has('timeseries') || viewingHistorical;
  const isMatch = match != null;

  // The dashboard always opens at the national view. A functional update so the
  // canvas size the camera carries survives the reset: the fly-to helpers fit
  // their bounds into it, and nothing re-measures the canvas until it resizes.
  React.useEffect(() => {
    if (isMatch) {
      setViewState(previous => ({
        ...previous,
        ...initialViewState,
        maxPitch: 85,
        transitionInterpolator: new FlyToInterpolator({ speed: 3.0 }),
        transitionDuration: 1000,
      }));
    }
  }, [isMatch, setViewState]);

  // These hooks are mounted by the shared Map component on every route, but the
  // products they draw only appear on the dashboard. `enabled` is therefore the
  // route, not the selected product: the sidebar's 24/7 Monitoring lists render
  // all four products' data whatever the selected map is, so switching products
  // must not wait on a fetch -- while an incident view pays for none of them.
  const productDataEnabled = isMatch;

  const wowLayer = useWowLayer({
    visible: match != null && props.selectedMap === 'wow' && !timeseriesEnabled,
    extruded: visualMode === '3d',
    enabled: productDataEnabled,
  });

  const nifcLayer = useNifcLayer({
    visible: match != null && props.selectedMap === 'nifc' && !timeseriesEnabled,
    extruded: visualMode === '3d',
  });

  const canopyLayer = useCanopyLayer({
    visible: match != null && props.selectedMap === 'canopy' && !timeseriesEnabled,
    extruded: visualMode === '3d',
    enabled: productDataEnabled,
  });

  // Deliberately not gated on the `timeseries` URL param like the layers
  // above: the swath layer is a base layer for the canopy view, hidden only
  // when the historical (_history) swaths render in its place.
  const canopySwathLayer = useCanopySwathLayer({
    visible: match != null && props.selectedMap === 'canopy' && !viewingHistorical,
    extruded: false,
    enabled: productDataEnabled,
  });

  const tingOutagesLayer = useTingOutagesLayer({
    visible: match != null && props.selectedMap === 'ting' && !timeseriesEnabled,
    enabled: productDataEnabled,
  });

  const layers = React.useMemo(
    () => [wowLayer, nifcLayer, canopyLayer, canopySwathLayer, tingOutagesLayer],
    [wowLayer, nifcLayer, canopyLayer, canopySwathLayer, tingOutagesLayer]
  );

  return layers;
};

export const HomeStats = React.memo(() => {
  return (
    <Box
      sx={{
        display: 'grid',
        padding: 1,
        top: 0,
        position: 'absolute',
        right: 0,

        '@media screen and (max-height: 768px), screen and (max-width: 768px)': {
          gridTemplateColumns: 'repeat(2, 1fr)',
          transform: 'scale(0.8)',
          transformOrigin: 'top right',
        },
      }}
    >
      <AdvisoryStats />
    </Box>
  );
});

export const HomeDrawer = React.memo(
  (props: {
    selectedMap: PrimaryLayer;
    setSelectedMap: React.Dispatch<React.SetStateAction<PrimaryLayer>>;
  }) => {
    return (
      <Box sx={{ padding: 0 }}>
        <Box sx={{ width: '100%', maxWidth: 300 }}>
          <Accordion defaultExpanded disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ alignItems: 'center', p: '0 8px' }}
            >
              <Box sx={{ p: 1, width: '100%' }}>
                <Box>
                  <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationImportantIcon sx={{ mr: 1 }} />
                    24/7 Monitoring
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '80%',
                    pt: 2,
                  }}
                >
                  <Tooltip title="Worst of the Worst">
                    <Paper
                      sx={{ borderRadius: '50%' }}
                      elevation={props.selectedMap === 'wow' ? 3 : 0}
                    >
                      <Avatar
                        alt="WOW"
                        sx={{
                          border: `1px solid rgb(0, 62, 103)`,
                          backgroundColor: 'rgb(255, 255, 255)',
                        }}
                        slotProps={{ img: { sx: { height: 'auto' } } }}
                        onClick={e => {
                          e.stopPropagation();
                          props.setSelectedMap('wow');
                        }}
                      >
                        <Typography variant="h6">WOW</Typography>
                      </Avatar>
                    </Paper>
                  </Tooltip>
                  <Tooltip title="National Interagency Fire Center">
                    <Paper
                      sx={{ borderRadius: '50%' }}
                      elevation={props.selectedMap === 'nifc' ? 3 : 0}
                    >
                      <Avatar
                        alt="NIFC"
                        sx={{
                          border: `1px solid rgb(0, 62, 103)`,
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          props.setSelectedMap('nifc');
                        }}
                      >
                        <NifcLogo width={40} height={40} />
                      </Avatar>
                    </Paper>
                  </Tooltip>
                  {enableCanopy && (
                    <Tooltip title="Canopy Tornado Alerts">
                      <Paper
                        sx={{ borderRadius: '50%' }}
                        elevation={props.selectedMap === 'canopy' ? 3 : 0}
                      >
                        <Avatar
                          alt="Canopy"
                          src={canopyLogo}
                          sx={{ border: '1px solid rgb(0, 62, 103)' }}
                          slotProps={{ img: { sx: { height: 'auto' } } }}
                          onClick={e => {
                            e.stopPropagation();
                            props.setSelectedMap('canopy');
                          }}
                        />
                      </Paper>
                    </Tooltip>
                  )}
                  <Tooltip title="Ting Power Outages">
                    <Paper
                      sx={{ borderRadius: '50%' }}
                      elevation={props.selectedMap === 'ting' ? 3 : 0}
                    >
                      <Avatar
                        alt="Ting"
                        sx={{
                          border: `1px solid rgb(0, 62, 103)`,
                          // The official Ting logo is white text with a yellow
                          // asterisk; it needs a dark field to be visible.
                          backgroundColor: 'rgb(0, 62, 103)',
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          props.setSelectedMap('ting');
                        }}
                      >
                        <TingLogo width={32} height={24} />
                      </Avatar>
                    </Paper>
                  </Tooltip>
                </Box>
              </Box>
            </AccordionSummary>

            {/* One boundary per product list. Each list renders rows straight
                out of a different live feed, which makes them the likeliest
                render-time failures on the page and independent of one another:
                a bad WoW row must not hide the fire perimeters next to it. */}
            <AccordionDetails>
              <Accordion variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'center' }}>
                  <Typography variant="h6">WOW (Worst of the Worst)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ErrorBoundary region="The WOW list">
                    <AlertsList
                      selectedMap="wow"
                      setSelectedMap={() => props.setSelectedMap('wow')}
                    />
                  </ErrorBoundary>
                </AccordionDetails>
              </Accordion>

              <Accordion variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'center' }}>
                  <Typography variant="h6">NIFC Events</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ErrorBoundary region="The NIFC list">
                    <AlertsList
                      selectedMap="nifc"
                      setSelectedMap={() => props.setSelectedMap('nifc')}
                    />
                  </ErrorBoundary>
                </AccordionDetails>
              </Accordion>
              {enableCanopy && (
                <Accordion variant="outlined" disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'center' }}>
                    <Typography variant="h6">Canopy</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ErrorBoundary region="The Canopy list">
                      <AlertsList
                        selectedMap="canopy"
                        setSelectedMap={() => props.setSelectedMap('canopy')}
                      />
                    </ErrorBoundary>
                  </AccordionDetails>
                </Accordion>
              )}
              <Accordion variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: 'center' }}>
                  <Typography variant="h6">Ting Power Outages</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ErrorBoundary region="The Ting outage list">
                    <TingOutageList setSelectedMap={() => props.setSelectedMap('ting')} />
                  </ErrorBoundary>
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>
          <ErrorBoundary region="The incident lists">
            <IncidentLists />
          </ErrorBoundary>
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    return prevProps?.selectedMap === nextProps?.selectedMap;
  }
);
